import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';
import { ShippingInfoCard, EMPTY_SHIPPING, cleanShipping } from '../components/ShippingInfoCard';
import { ChargeLibrary, chargeToLineItem } from '../components/ChargeLibrary';
import { RateCardLibrary } from '../components/RateCardLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateInvoice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCash = searchParams.get('cash') === '1';
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [gstCfg, setGstCfg] = useState({ enabled: false, rate: 0 });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings/gst`).then((res) => setGstCfg(res.data)).catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    po_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    payment_terms: 'COD - Cash on Delivery',
    notes: '',
    quotation_id: ''
  });
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0, rate_type: '' }
  ]);
  const [shipping, setShipping] = useState(EMPTY_SHIPPING);

  useEffect(() => {
    fetchPricing();
    fetchQuotations();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations-billing/`);
      setQuotations(response.data.filter(q => q.status === 'accepted'));
    } catch (error) {
      console.error(error);
    }
  };

  const loadFromQuotation = async (quotationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations-billing/${quotationId}`);
      const quotation = response.data;
      
      setFormData({
        ...formData,
        customer_name: quotation.customer_name,
        customer_email: quotation.customer_email,
        customer_phone: quotation.customer_phone,
        customer_address: quotation.customer_address || '',
        quotation_id: quotationId
      });
      
      setLineItems(quotation.line_items);
    } catch (error) {
      toast.error('Failed to load quotation');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, amount: 0, rate_type: '' }]);
  };

  const removeLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price' || field === 'hours') {
      const qty = parseFloat(updated[index].quantity || 0);
      const hours = parseFloat(updated[index].hours || 0);
      const price = parseFloat(updated[index].unit_price || 0);
      updated[index].amount = hours > 0 ? qty * hours * price : qty * price;
    }
    if (field === 'amount') {
      updated[index].amount = value;
    }
    
    setLineItems(updated);
  };

  const selectService = (index, serviceId, rateType) => {
    if (!pricing) return;
    
    let service = null;
    const allServices = [
      ...(pricing.services.lorry_cranes || []),
      ...(pricing.services.trailers || []),
      ...(pricing.services.tailgate || [])
    ];
    
    service = allServices.find(s => s.id === serviceId);
    if (serviceId === 'manpower') {
      service = pricing.services.manpower;
    }
    if (serviceId === 'warehouse_storage') {
      service = pricing.services.warehouse_storage;
    }
    if (serviceId === 'handling_fee') {
      service = pricing.services.handling_fee;
    }
    
    if (!service) return;
    
    const updated = [...lineItems];
    let unitPrice = 0;
    let description = service.name;
    
    if (rateType === 'hourly' && service.hourly_rate) {
      unitPrice = service.hourly_rate;
      description += ' - Hourly Rate';
    } else if (rateType === 'half_day' && service.half_day_rate) {
      unitPrice = service.half_day_rate;
      description += ' - Half Day';
    } else if (rateType === 'per_trip' && service.per_trip_rate) {
      unitPrice = service.per_trip_rate;
      description += ' - Per Trip';
    } else if (rateType === 'per_day' && service.per_day_rate) {
      unitPrice = service.per_day_rate;
      description += ' - Per Day';
    } else if (rateType === 'as_quoted') {
      unitPrice = 0;
      description += ' - As Quoted';
    }
    
    updated[index].description = description;
    updated[index].unit_price = unitPrice;
    updated[index].rate_type = rateType;
    const qty = parseFloat(updated[index].quantity || 0);
    const hours = parseFloat(updated[index].hours || 0);
    updated[index].amount = hours > 0 ? qty * hours * unitPrice : qty * unitPrice;
    
    setLineItems(updated);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const invoiceData = {
        ...formData,
        is_cash: isCash,
        payment_terms: isCash ? 'CASH — payment upon completion' : formData.payment_terms,
        shipping_info: cleanShipping(shipping),
        line_items: lineItems.map(item => ({
          description: item.description,
          lorry_no: item.lorry_no || '',
          uom: item.uom || '',
          do_number: item.do_number || '',
          quantity: parseFloat(item.quantity),
          hours: parseFloat(item.hours) || 0,
          unit_price: parseFloat(item.unit_price),
          amount: parseFloat(item.amount) || 0
        }))
      };

      const response = await axios.post(`${BACKEND_URL}/api/invoices/`, invoiceData);

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/admin/billing');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create invoice'));
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculateSubtotal();
  const gstAmt = gstCfg.enabled ? +(subtotal * (gstCfg.rate || 0) / 100).toFixed(2) : 0;

  const addChargeFromLibrary = (c) => {
    const newItem = { ...chargeToLineItem(c, calculateSubtotal()), rate_type: '' };
    setLineItems((prev) => {
      if (prev.length === 1 && !prev[0].description && !(prev[0].amount > 0)) return [newItem];
      return [...prev, newItem];
    });
  };

  const addRateFromCard = (item) => {
    const newItem = { ...item, lorry_no: '', uom: '', do_number: '', rate_type: '' };
    setLineItems((prev) => {
      if (prev.length === 1 && !prev[0].description && !(prev[0].amount > 0)) return [newItem];
      return [...prev, newItem];
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                {isCash ? 'Create Cash Invoice' : 'Create Invoice'}
                {isCash && <span className="text-sm font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-3 py-1" data-testid="cash-invoice-badge">CASH</span>}
              </h1>
              <p className="text-gray-600 mt-1">Generate a new invoice for customer</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {quotations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Import from Quotation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select onValueChange={loadFromQuotation}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select quotation..." />
                      </SelectTrigger>
                      <SelectContent>
                        {quotations.map(q => (
                          <SelectItem key={q.id} value={q.id}>
                            {q.document_number} - {q.customer_name} (${q.total_amount.toFixed(2)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        required
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        required
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={formData.customer_address}
                      onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                      rows={3}
                      data-testid="customer-address-input"
                    />
                  </div>
                  <div>
                    <Label>Customer P/O Number</Label>
                    <Input
                      value={formData.po_number}
                      onChange={(e) => setFormData({...formData, po_number: e.target.value})}
                      placeholder="Purchase Order number (optional)"
                      data-testid="po-number-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Line Items</CardTitle>
                    <Button type="button" onClick={addLineItem} variant="outline">
                      <Plus size={16} className="mr-2" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <RateCardLibrary onAdd={addRateFromCard} />
                  <ChargeLibrary onAdd={addChargeFromLibrary} />
                  <div className="space-y-4">
                    {lineItems.map((item, index) => (
                      <div key={index} className="border p-4 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold">Item {index + 1}</p>
                          {lineItems.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>

                        {pricing && (
                          <div>
                            <Label>Quick Select Service</Label>
                            <select
                              className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white"
                              onChange={(e) => {
                                const [serviceId, rateType] = e.target.value.split('|');
                                if (serviceId && rateType) {
                                  selectService(index, serviceId, rateType);
                                }
                              }}
                              defaultValue=""
                            >
                              <option value="">Select service...</option>
                              <optgroup label="Lorry Cranes">
                                {pricing.services.lorry_cranes?.map(s => (
                                  <React.Fragment key={s.id}>
                                    {s.hourly_rate && <option value={`${s.id}|hourly`}>{s.name} - Hourly</option>}
                                    {s.per_trip_rate && <option value={`${s.id}|per_trip`}>{s.name} - Per Trip</option>}
                                    {s.per_day_rate && <option value={`${s.id}|per_day`}>{s.name} - Per Day</option>}
                                    {s.half_day_rate && <option value={`${s.id}|half_day`}>{s.name} - Half Day</option>}
                                  </React.Fragment>
                                ))}
                              </optgroup>
                              <optgroup label="Trailers">
                                {pricing.services.trailers?.map(s => (
                                  <React.Fragment key={s.id}>
                                    {s.hourly_rate && <option value={`${s.id}|hourly`}>{s.name} - Hourly</option>}
                                    {s.half_day_rate && <option value={`${s.id}|half_day`}>{s.name} - Half Day</option>}
                                    {s.per_trip_rate && <option value={`${s.id}|per_trip`}>{s.name} - Per Trip</option>}
                                    {s.per_day_rate && <option value={`${s.id}|per_day`}>{s.name} - Per Day</option>}
                                  </React.Fragment>
                                ))}
                              </optgroup>
                              <optgroup label="Tailgate">
                                {pricing.services.tailgate?.map(s => (
                                  <React.Fragment key={s.id}>
                                    {s.hourly_rate && <option value={`${s.id}|hourly`}>{s.name} - Hourly</option>}
                                    {s.half_day_rate && <option value={`${s.id}|half_day`}>{s.name} - Half Day</option>}
                                    {s.per_trip_rate && <option value={`${s.id}|per_trip`}>{s.name} - Per Trip</option>}
                                    {s.per_day_rate && <option value={`${s.id}|per_day`}>{s.name} - Per Day</option>}
                                  </React.Fragment>
                                ))}
                              </optgroup>
                              <optgroup label="Other Services">
                                <option value="manpower|per_day">Manpower - Per Day</option>
                                <option value="warehouse_storage|as_quoted">Warehouse Storage - As Quoted</option>
                                <option value="handling_fee|as_quoted">Handling Fee (Loading/Unloading) - As Quoted</option>
                              </optgroup>
                            </select>
                          </div>
                        )}

                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-2">
                            <Label>Description *</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                              required
                              data-testid={`line-item-description-${index}`}
                            />
                            <Input
                              value={item.do_number || ''}
                              onChange={(e) => updateLineItem(index, 'do_number', e.target.value)}
                              placeholder="DO No. e.g. DO-131"
                              className="mt-1 h-8 text-xs"
                              data-testid={`line-item-do-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Lorry No.</Label>
                            <Input
                              value={item.lorry_no || ''}
                              onChange={(e) => updateLineItem(index, 'lorry_no', e.target.value)}
                              placeholder="XD1234A"
                              data-testid={`line-item-lorry-${index}`}
                            />
                            <Input
                              value={item.uom || ''}
                              onChange={(e) => updateLineItem(index, 'uom', e.target.value)}
                              placeholder="UOM e.g. 20' / TRK (container invoices)"
                              className="mt-1 h-8 text-xs"
                              data-testid={`line-item-uom-${index}`}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label>Qty</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Hours</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.hours || ''}
                              onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                              placeholder="0"
                              data-testid={`line-item-hours-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Unit Price ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Amount ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.amount}
                              onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                              data-testid={`line-item-amount-${index}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Payment Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Invoice Date (Job Request Date)</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                      data-testid="invoice-date-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Date when the customer requested the job</p>
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Input
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                      placeholder="COD - Cash on Delivery"
                      data-testid="payment-terms-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Default: COD - Cash on Delivery. Editable to any term (Net 7, Net 30, etc.)</p>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <ShippingInfoCard value={shipping} onChange={setShipping} />
            </div>

            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Invoice Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">GST ({gstCfg.enabled ? gstCfg.rate : 0}%):</span>
                      <span className="font-semibold">${gstAmt.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-xl text-green-600">${(subtotal + gstAmt).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      disabled={loading}
                      data-testid="create-invoice-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={20} />
                          Create Invoice
                        </span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};